import React, { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Copy, History, Loader2, Save, Sparkles, Trophy } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { nomeGrupo, Sorteio } from "./analiseEngine";
import { gerarHistoricoPremiacoes, gerarPalpitesAvancados, HistoricoPalpiteItem, HistoricoPremiacoes, rotuloFonte, rotuloPremios, rotuloValidade, salvarPalpites } from "./palpitesUtils";
import { analisarDezenasFrenteFundo, AnaliseFrenteFundo } from "./frenteFundoEngine";
import DezenasFrenteFundoCard from "./DezenasFrenteFundoCard";

interface Props {
  sorteios: Sorteio[];
  fontes: string[];
  filtros: Record<string, unknown>;
}

const PalpitesAvancadosCards: React.FC<Props> = ({ sorteios, fontes, filtros }) => {
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const palpites = useMemo(() => gerarPalpitesAvancados(sorteios), [sorteios]);
  const historicosPorFonte = useMemo(() => Object.fromEntries(
    fontes.map((fonte) => [fonte, gerarHistoricoPremiacoes(palpites, sorteios.filter((sorteio) => sorteio.fonte === fonte))])
  ) as Record<string, HistoricoPremiacoes>, [fontes, palpites, sorteios]);
  const frenteFundoPorFonte = useMemo(() => Object.fromEntries(
    fontes.map((fonte) => [fonte, analisarDezenasFrenteFundo(sorteios, fonte)])
  ) as Record<string, AnaliseFrenteFundo>, [fontes, sorteios]);

  const sections = [
    { title: "Milhares", items: palpites.milhares || [] },
    { title: "Centenas", items: palpites.centenas || [] },
    { title: "Duques de dezenas", items: palpites.duques_dezenas || [] },
    { title: "Duques de grupos", items: palpites.duques_grupos || [], grupos: true },
    { title: "Ternos de dezenas", items: palpites.ternos_dezenas || [] },
    { title: "Ternos de grupos", items: palpites.ternos_grupos || [], grupos: true },
    { title: "Grupos", items: (palpites.grupos || []).map(String), grupos: true },
  ];

  const fontesValidas = fontes.filter((fonte) => fonte === "rio" || fonte === "federal");
  const premios = rotuloPremios(filtros.posicoes);
  const turnos = Array.isArray(filtros.turnos) && filtros.turnos.length
    ? filtros.turnos.join(", ")
    : "Todos os turnos";

  const formatItem = (item: string, grupos?: boolean) => {
    if (!grupos) return item.split("-").join(" + ");
    return item.split("-").map((g) => nomeGrupo(Number(g))).join(" + ");
  };

  const copyAll = async () => {
    const contexto = fontesValidas.map((fonte) => [
      `Loteria: ${rotuloFonte(fonte)}`,
      `Prêmios: ${premios}`,
      ...(fonte === "rio" ? [`Turnos: ${turnos}`] : []),
      `Validade: ${rotuloValidade(fonte)}`,
    ].join("\n")).join("\n\n");
    const jogos = sections.map((s) => `${s.title}: ${s.items.map((i) => formatItem(i, s.grupos)).join(", ")}`).join("\n");
    const frenteFundo = fontesValidas.map((fonte) => {
      const analise = frenteFundoPorFonte[fonte];
      if (!analise) return "";
      return [
        `Dezenas Frente e Fundo — ${rotuloFonte(fonte)}`,
        `Frentes: ${analise.frentes.map((item) => item.valor).join(", ")}`,
        `Fundos: ${analise.fundos.map((item) => item.valor).join(", ")}`,
        `Milhares fortes: ${analise.milhares.map((item) => `${item.milhar} (${item.pontuacao}/100)`).join(", ")}`,
      ].join("\n");
    }).filter(Boolean).join("\n\n");
    const text = `${contexto}\n\n${jogos}\n\n${frenteFundo}`;
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast({ title: "Palpites copiados" });
  };

  const saveAll = async () => {
    const validSources = fontes.filter((f): f is "rio" | "federal" => f === "rio" || f === "federal");
    if (!validSources.length) return;
    setSaving(true);
    try {
      await Promise.all(validSources.map((source) => salvarPalpites({
        palpites,
        source,
        origem: "avancada",
        filtros: {
          ...filtros,
          historico_premiacoes: historicosPorFonte[source],
          dezenas_frente_fundo: frenteFundoPorFonte[source],
        },
      })));
      toast({ title: "Palpites salvos", description: validSources.length > 1 ? "Foi criado um jogo para cada loteria." : "Pronto para conferir os próximos resultados." });
    } catch (error) {
      toast({ title: "Não foi possível salvar", description: error instanceof Error ? error.message : "Tente novamente", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  if (!sorteios.length) return null;

  return (
    <div className="space-y-3 rounded-lg border border-yellow-500/40 bg-black/30 p-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="flex items-center gap-2 text-sm font-bold text-yellow-300"><Sparkles className="h-4 w-4" /> Palpites automáticos do recorte</p>
          <p className="text-[11px] text-yellow-100/70">Atualizados automaticamente pelos filtros da análise.</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={copyAll} className="gap-1.5">
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} {copied ? "Copiado" : "Copiar"}
          </Button>
          <Button size="sm" onClick={saveAll} disabled={saving} className="gap-1.5">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Salvar
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {fontesValidas.map((fonte) => (
          <div key={fonte} className="rounded-md border border-yellow-500/30 bg-background/80 p-3 text-xs">
            <p className="font-bold text-yellow-500">{rotuloFonte(fonte)}</p>
            <p className="mt-1 text-foreground"><span className="font-semibold">Prêmios:</span> {premios}</p>
            {fonte === "rio" && <p className="text-foreground"><span className="font-semibold">Turnos:</span> {turnos}</p>}
            <p className="mt-1 text-muted-foreground"><span className="font-semibold text-foreground">Validade:</span> {rotuloValidade(fonte)}</p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {sections.map((section) => (
          <Card key={section.title} className="border-yellow-500/30 bg-background/90">
            <CardHeader className="p-3 pb-1"><CardTitle className="text-xs text-yellow-500">{section.title}</CardTitle></CardHeader>
            <CardContent className="flex flex-wrap gap-1.5 p-3 pt-1">
              {section.items.map((item) => (
                <Badge key={item} variant="secondary" className="h-auto whitespace-normal py-1 font-mono text-[11px]">
                  {formatItem(item, section.grupos)}
                </Badge>
              ))}
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="space-y-2">
        {fontesValidas.map((fonte) => frenteFundoPorFonte[fonte] && (
          <DezenasFrenteFundoCard key={`frente-fundo-${fonte}`} analise={frenteFundoPorFonte[fonte]} />
        ))}
      </div>
      <div className="rounded-md border border-yellow-500/30 bg-background/80 p-3">
        <p className="flex items-center gap-2 text-xs font-bold text-yellow-500">
          <History className="h-4 w-4" /> Histórico de premiações destes palpites
        </p>
        <p className="mt-1 text-[11px] text-muted-foreground">
          Milhares, centenas, duques e ternos de dezenas: últimos 21 dias.
        </p>
        {fontesValidas.map((fonte) => (
          <div key={`historico-${fonte}`} className="mt-3">
            <p className="mb-1 text-[11px] font-bold text-yellow-500">{rotuloFonte(fonte)}</p>
            <HistoricoPremiacoesDetalhes historico={historicosPorFonte[fonte]} />
          </div>
        ))}
        <p className="mt-3 border-t border-border pt-2 text-[10px] text-muted-foreground">
          O histórico e os padrões estatísticos explicam a seleção, mas não garantem premiação futura.
        </p>
      </div>
    </div>
  );
};

const TITULOS_HISTORICO: Record<string, string> = {
  milhares: "Milhares",
  centenas: "Centenas",
  duques_dezenas: "Duques de dezenas",
  ternos_dezenas: "Ternos de dezenas",
};

export const HistoricoPremiacoesDetalhes: React.FC<{ historico?: HistoricoPremiacoes }> = ({ historico }) => historico ? (
  <div className="mt-3 grid grid-cols-1 gap-2 lg:grid-cols-2">
    {Object.entries(historico.modalidades).map(([chave, modalidade]) => modalidade && (
      <div key={chave} className="rounded-md border border-border bg-muted/30 p-2.5">
        <p className="mb-2 text-[11px] font-bold text-foreground">{TITULOS_HISTORICO[chave]} · {modalidade.janela_dias} dias</p>
        <div className="space-y-2">
          {modalidade.itens.map((item: HistoricoPalpiteItem) => (
            <details key={`${chave}-${item.valor}`} className="rounded-md border border-border bg-background px-2.5 py-2">
              <summary className="cursor-pointer list-none text-xs font-semibold text-foreground">
                <span className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-mono">{item.valor.split("-").join(" + ")}</span>
                  <Badge variant={item.total > 0 ? "default" : "outline"} className="gap-1 text-[10px]">
                    {item.total > 0 && <Trophy className="h-3 w-3" />}
                    {item.total > 0 ? `${item.total} premiação(ões)` : "Sem premiação"}
                  </Badge>
                </span>
              </summary>
              <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground"><strong className="text-foreground">Por que foi escolhido:</strong> {item.justificativa}</p>
              {item.ocorrencias.length > 0 && (
                <div className="mt-2 space-y-1 border-t border-border pt-2">
                  {item.ocorrencias.map((ocorrencia, indice) => (
                    <p key={`${ocorrencia.data}-${ocorrencia.turno}-${indice}`} className="text-[11px] text-foreground">
                      {new Date(`${ocorrencia.data}T12:00:00`).toLocaleDateString("pt-BR")} · {ocorrencia.turno} · prêmio(s) {ocorrencia.posicoes.map((posicao) => `${posicao}º`).join(", ")}
                    </p>
                  ))}
                </div>
              )}
            </details>
          ))}
        </div>
      </div>
    ))}
  </div>
) : null;

export default PalpitesAvancadosCards;